import { useEffect, useState } from 'react'

import { useOnboarding } from 'components/member_onboarding_v2/OnboardingContext'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlans as useGetMedicalPlans } from 'utils/web_api'

import { PremiumRangeSlider } from './PremiumRangeSlider'
import { type PlanSelectionPreference } from './type'
import { type MoneyAmount } from '../../types'

type MaximumOutOfPocketBudgetProps = {
  value?: PlanSelectionPreference['oop_budget'] | null;
  setValue: (value: PlanSelectionPreference['oop_budget'] | null) => void;
  allowanceAmount: MoneyAmount;
}

export function MaximumOutOfPocketBudgetV2 ({
  value,
  setValue,
  allowanceAmount,
}: MaximumOutOfPocketBudgetProps) {
  const { enrollmentEventId } = useOnboarding()
  const [priceRange, setPriceRange] = useState<{
    min: number;
    max: number;
  } | null>(null)

  const { data: medicalPlanData, isLoading: medicalPlanLoading } =
    useGetMedicalPlans(
      enrollmentEventId,
      {
        'sort[]': ['bucketed_premium'],
        'page[number]': 1,
        'page[size]': 1000,
      },
      {
        query: {
          enabled: !priceRange,
          queryKey: [
            'enrollmentEvent',
            enrollmentEventId,
            'medical_plans_price_range',
          ],
        },
      }
    )

  useEffect(() => {
    if (medicalPlanData?.data?.length) {
      const lowestPlan = medicalPlanData.data[0]
      const higherPlan = medicalPlanData.data[medicalPlanData.data.length - 1]
      setPriceRange({
        min: (lowestPlan.total_premium?.subunits ?? 0) / 100,
        max: (higherPlan.total_premium?.subunits ?? 0) / 100,
      })
    }
  }, [medicalPlanData?.data])

  const allowanceInDollars = Number(allowanceAmount?.value ?? 0)
  const selectedOop = Number(value ?? priceRange?.max ?? 0)

  useEffect(() => {
    // If the selected value is equilvant or higher than the maximum price,
    // then we set the value to null to indicate "no limit" to the contribution
    if (priceRange && allowanceInDollars + selectedOop >= priceRange.max) {
      setValue(null)
    }
  }, [allowanceInDollars, selectedOop, priceRange?.max])

  if (!medicalPlanLoading && (!priceRange || isNaN(allowanceInDollars))) {
    return null
  }

  const allowanceCanCoverAllPlans =
    priceRange && allowanceInDollars >= priceRange.max
  const title = allowanceCanCoverAllPlans
    ? "You're fully covered"
    : 'How much are you willing to contribute out-of-pocket?'
  const text = allowanceCanCoverAllPlans
    ? "Your allowance covers every plan in your area, so you won't have any out-of-pocket costs."
    : 'Insurance plans that cover a greater share of healthcare costs will have higher monthly premiums. ' +
      'By adding some money to be spent out of pocket on top of your allowance, ' +
      'we may be able to find a plan for you with better coverage for only a little extra each month.'

  const renderOOPDisplayText = () => {
    if (allowanceCanCoverAllPlans) {
      return (
        <>
          You'll pay{' '}
          <span className='font-semibold text-lg leading-7 text-[#832ed9]'>
            $0
          </span>{' '}
          /month
        </>
      )
    }

    if (value === null) {
      return (
        <span className='font-semibold text-lg leading-7 text-[#832ed9]'>
          No limit
        </span>
      )
    }

    return (
      <>
        Up to{' '}
        <span
          data-testid='oop-budget'
          className='font-semibold text-lg leading-7 text-[#832ed9]'
        >
          ${selectedOop}
        </span>{' '}
        /month
      </>
    )
  }

  return (
    <div data-testid='MaximumOutOfPocketBudgetV2'>
      <h3 className='heading-sm text-gray-800 mb-2'>
        {title}
      </h3>
      <p className='body text-gray-800 mb-6'>{text}</p>

      {medicalPlanLoading && (
        <div
          data-testid='MaximumOutOfPocketBudgetV2Loading'
          className='flex items-center justify-center'
        >
          <LoadingSpinnerCentered />
        </div>
      )}
      {!medicalPlanLoading && !!priceRange && (
        <div className='flex flex-col items-center'>
          <p className='font-normal text-sm leading-5 text-gray-800 mb-4'>
            {renderOOPDisplayText()}
          </p>

          <PremiumRangeSlider
            minPrice={priceRange.min}
            maxPrice={priceRange.max}
            allowanceAmount={allowanceInDollars}
            value={selectedOop}
            onChange={setValue}
          />
        </div>
      )}
    </div>
  )
}
